import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Clock, ArrowRight, Infinity as InfinityIcon } from "lucide-react";

/**
 * ExpiryBanner
 * Fetches /participant/me and shows an amber warning banner when the user's
 * access is about to expire (< thresholdDays days remaining). No banner is
 * shown for lifetime users or when access is > threshold days away.
 */
export default function ExpiryBanner({ thresholdDays = 7 }) {
  const [me, setMe] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/participant/me");
        setMe(data);
      } catch (_) { /* not logged in / stale token — hide */ }
    })();
  }, []);

  if (!me || dismissed) return null;
  if (!me.expiry_date) return null; // lifetime — nothing to warn

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(me.expiry_date + "T00:00:00");
  const diffMs = expiry.getTime() - today.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysLeft > thresholdDays) return null;

  const isExpired = daysLeft < 0;
  const isToday = daysLeft === 0;

  return (
    <div
      className={`rounded-xl border p-4 flex items-center gap-4 flex-wrap ${
        isExpired
          ? "border-red-500/40 bg-gradient-to-r from-red-500/15 to-red-500/5"
          : "border-amber-500/40 bg-gradient-to-r from-amber-500/15 to-amber-500/5"
      }`}
      data-testid="expiry-banner"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        isExpired ? "bg-red-500/15 border border-red-500/40" : "bg-amber-500/15 border border-amber-500/40"
      }`}>
        {me.expiry_date ? (
          <Clock size={18} className={isExpired ? "text-red-400" : "text-amber-400"} />
        ) : (
          <InfinityIcon size={18} className="text-amber-400" />
        )}
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className={`font-display text-base font-bold ${isExpired ? "text-red-300" : "text-amber-300"}`}>
          {isExpired
            ? "Akses anda telah tamat"
            : isToday
              ? "Akses anda tamat hari ini"
              : `Akses tamat dalam ${daysLeft} hari`}
        </div>
        <div className="text-xs text-neutral-400 mt-0.5 font-mono">
          Tarikh tamat: <span className="text-white">{me.expiry_date}</span> · Sambung sekarang untuk elak gangguan.
        </div>
      </div>
      <Link
        to="/pricing"
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
          isExpired
            ? "bg-red-500 text-black hover:bg-red-400"
            : "bg-amber-500 text-black hover:bg-amber-400"
        }`}
        data-testid="expiry-banner-cta"
      >
        Sambung Akses <ArrowRight size={14} />
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="text-xs text-neutral-500 hover:text-neutral-300 px-2"
        data-testid="expiry-banner-dismiss"
        aria-label="Tutup"
      >
        Tutup
      </button>
    </div>
  );
}
